import {bbox} from '@turf/turf';
import {throttle} from 'lodash';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React from 'react';
import {Layer, Map as MapGL, MapRef, Popup, Source} from 'react-map-gl/mapbox';
import colors from 'tailwindcss/colors';

import {useResizeMapOnChange} from 'src/hooks/useMapResize';
import type {PublicGauge} from 'src/lib/api/setupWizard';
import type {InteractiveMouseEvent} from 'src/lib/map';
import {MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE_IDS, mapboxStyleUrl} from 'src/lib/map';
import type {SiteInfoRow} from 'src/lib/setupWizardConstants';

const BASIN_PALETTE = [
  colors.blue[500],
  colors.emerald[500],
  colors.amber[500],
  colors.rose[500],
  colors.violet[500],
];

interface Props {
  basins: GeoJSON.FeatureCollection;
  rivers: GeoJSON.FeatureCollection | null;
  siteInfo: SiteInfoRow[];
  publicGauges?: PublicGauge[] | null;
  contributingSites?: string[];
  /** Names used for the color palette and legend (typically outlet/basin names). */
  basinGroupNames?: string[];
  /** Maps each subbasin site_name to its basin group name for coloring. */
  subbasinToBasinGroup?: Record<string, string>;
  /** Drainage area in km² per basin group, shown in the legend. */
  drainageAreaByBasinGroup?: Record<string, number>;
}

type BasemapId = 'light' | 'satellite' | 'streets';

const STYLE_ID_BY_BASEMAP: Record<BasemapId, string> = {
  light: MAPBOX_STYLE_IDS.light,
  streets: MAPBOX_STYLE_IDS.streets,
  satellite: MAPBOX_STYLE_IDS.satelliteStreets,
};

const INTERACTIVE_LAYER_IDS = [
  'basins-fill',
  'sites-circle',
  'private-site-gauges-circle',
  'ungauged-sites-circle',
  'public-gauges-circle',
];

function normalizeSiteName(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
}

function toNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function withNormalizedSiteNames(
  basins: GeoJSON.FeatureCollection,
  subbasinToBasinGroup: Record<string, string>
): GeoJSON.FeatureCollection {
  return {
    ...basins,
    features: basins.features.map((feature) => {
      const properties = feature.properties ?? {};
      const siteName = (properties as Record<string, unknown>).site_name;
      return {
        ...feature,
        properties: {
          ...properties,
          site_name_normalized: normalizeSiteName(siteName),
          basin_group: typeof siteName === 'string' ? (subbasinToBasinGroup[siteName] ?? '') : '',
        },
      };
    }),
  };
}

function buildForecastPointsGeoJson(siteInfo: SiteInfoRow[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const row of siteInfo) {
    if (!row.paid_for) continue;
    const lat = row.lat;
    const lon = row.lon;
    if (lat == null || lon == null) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      properties: {
        siteName: row.name ?? '',
        latitude: lat,
        longitude: lon,
        requestedForecasts: row.paid_for ?? null,
      },
    });
  }
  return {
    type: 'FeatureCollection',
    features,
  };
}

function buildPrivateSiteGaugesGeoJson(siteInfo: SiteInfoRow[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const row of siteInfo) {
    if (row.gauge_type !== 'Private') continue;
    const lat = row.lat;
    const lon = row.lon;
    if (lat == null || lon == null) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      properties: {
        siteName: row.name ?? '',
        latitude: lat,
        longitude: lon,
      },
    });
  }
  return {
    type: 'FeatureCollection',
    features,
  };
}

function buildUngaugedSitesGeoJson(siteInfo: SiteInfoRow[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const row of siteInfo) {
    if (row.gauge_type || row.are_observations_available) continue;
    const lat = row.lat;
    const lon = row.lon;
    if (lat == null || lon == null) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      properties: {
        siteName: row.name ?? '',
        latitude: lat,
        longitude: lon,
      },
    });
  }
  return {
    type: 'FeatureCollection',
    features,
  };
}

function buildPublicGaugesGeoJson(
  publicGauges: PublicGauge[] | null | undefined
): GeoJSON.FeatureCollection {
  if (!publicGauges || publicGauges.length === 0) {
    return {type: 'FeatureCollection', features: []};
  }
  const features: GeoJSON.Feature[] = [];
  for (const gauge of publicGauges) {
    const lat = toNumber(gauge['lat']);
    const lon = toNumber(gauge['lng'] ?? gauge['lon']);
    if (lat == null || lon == null) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      properties: {
        name: gauge['name'] ?? null,
        gaugeType: gauge['gauge_type'] ?? null,
        gaugeId: gauge['gauge_id'] ?? null,
        latitude: lat,
        longitude: lon,
      },
    });
  }
  return {
    type: 'FeatureCollection',
    features,
  };
}

function buildFillColorExpression(basinGroupNames: string[]): mapboxgl.Expression | string {
  if (basinGroupNames.length <= 1) return BASIN_PALETTE[0];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const expr: any[] = ['match', ['get', 'basin_group']];
  for (let i = 0; i < basinGroupNames.length; i++) {
    expr.push(basinGroupNames[i], BASIN_PALETTE[i % BASIN_PALETTE.length]);
  }
  expr.push(BASIN_PALETTE[0]); // fallback
  return expr as mapboxgl.Expression;
}

const LABEL_LAYOUT = {
  'text-field': ['get', 'siteName'],
  'text-size': 11,
  'text-offset': [0, 1.2],
  'text-anchor': 'top',
} as const;

const LABEL_PAINT = {
  'text-color': '#111827',
  'text-halo-color': '#ffffff',
  'text-halo-width': 1,
} as const;

/*
Stable layout/paint references: react-map-gl deep-compares each Layer's props
per render, and hover re-renders the component, so fresh objects here would be
re-diffed on every popup update.
*/
const VISIBLE = {visibility: 'visible'} as const;
const HIDDEN = {visibility: 'none'} as const;
const visibility = (isVisible: boolean) => (isVisible ? VISIBLE : HIDDEN);

const LABEL_LAYOUT_VISIBLE = {...LABEL_LAYOUT, ...VISIBLE};
const LABEL_LAYOUT_HIDDEN = {...LABEL_LAYOUT, ...HIDDEN};
const labelLayout = (isVisible: boolean) =>
  isVisible ? LABEL_LAYOUT_VISIBLE : LABEL_LAYOUT_HIDDEN;

const RIVERS_LINE_PAINT = {'line-color': '#0ea5e9', 'line-width': 1};
const BASINS_HIGHLIGHT_PAINT = {'line-color': '#000000', 'line-width': 3};

const SITES_CIRCLE_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': 5,
  'circle-color': '#ef4444',
  'circle-stroke-width': 2,
  'circle-stroke-color': '#b91c1c',
};

const PRIVATE_SITE_GAUGES_CIRCLE_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': 5,
  'circle-color': '#f97316',
  'circle-stroke-width': 2,
  'circle-stroke-color': '#c2410c',
};

const UNGAUGED_SITES_CIRCLE_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': 5,
  'circle-color': '#9ca3af',
  'circle-stroke-width': 2,
  'circle-stroke-color': '#6b7280',
};

const PUBLIC_GAUGES_CIRCLE_PAINT: mapboxgl.CirclePaint = {
  'circle-radius': 4,
  'circle-color': '#6d28d9',
  'circle-stroke-width': 1.5,
  'circle-stroke-color': '#4c1d95',
};

type HoverPopup = {
  longitude: number;
  latitude: number;
  title: string;
  rows: Array<[string, unknown]>;
};

/*
Hover-popup content per interactive layer: a title and labeled rows read from
the hovered feature's properties.
*/
function popupForFeature(
  layerId: string,
  p: Record<string, unknown>
): Pick<HoverPopup, 'title' | 'rows'> {
  switch (layerId) {
    case 'basins-fill':
      return {
        title: 'Subbasin',
        rows: [
          ['Forecast point', p.site_name],
          ['Upstream area (km²)', p.upstream_area_km2],
          ['Node type', p.node_type],
          ['Node index', p.node_index],
        ],
      };
    case 'sites-circle':
    case 'ungauged-sites-circle':
      return {
        title: 'Forecast point',
        rows: [
          ['Name', p.siteName],
          ['Latitude', p.latitude],
          ['Longitude', p.longitude],
          ['Requested forecasts', p.requestedForecasts],
        ],
      };
    case 'private-site-gauges-circle':
      return {
        title: 'Private gauge',
        rows: [
          ['Name', p.siteName],
          ['Latitude', p.latitude],
          ['Longitude', p.longitude],
        ],
      };
    case 'public-gauges-circle':
    default:
      return {
        title: 'Public gauge',
        rows: [
          ['Name', p.name],
          ['Gauge type', p.gaugeType],
          ['Gauge ID', p.gaugeId],
          ['Latitude', p.latitude],
          ['Longitude', p.longitude],
        ],
      };
  }
}

export default function DelineationMap({
  basins,
  rivers,
  siteInfo,
  publicGauges,
  contributingSites = [],
  basinGroupNames = [],
  subbasinToBasinGroup = {},
  drainageAreaByBasinGroup = {},
}: Props) {
  const mapRef = React.useRef<MapRef>(null);
  const [basemapId, setBasemapId] = React.useState<BasemapId>('light');
  const [expanded, setExpanded] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [hoverPopup, setHoverPopup] = React.useState<HoverPopup | null>(null);
  const [layerVisibility, setLayerVisibility] = React.useState({
    basins: true,
    rivers: true,
    forecastPoints: true,
    privateSiteGauges: true,
    ungaugedSites: true,
    publicGauges: true,
  });

  const styleUrl = mapboxStyleUrl(STYLE_ID_BY_BASEMAP[basemapId]);

  const basinsData = React.useMemo(
    () => withNormalizedSiteNames(basins, subbasinToBasinGroup),
    [basins, subbasinToBasinGroup]
  );
  const forecastPointsData = React.useMemo(() => buildForecastPointsGeoJson(siteInfo), [siteInfo]);
  const privateSiteGaugesData = React.useMemo(
    () => buildPrivateSiteGaugesGeoJson(siteInfo),
    [siteInfo]
  );
  const ungaugedSitesData = React.useMemo(() => buildUngaugedSitesGeoJson(siteInfo), [siteInfo]);
  const publicGaugesData = React.useMemo(
    () => buildPublicGaugesGeoJson(publicGauges),
    [publicGauges]
  );

  const bounds = React.useMemo(() => bbox(basins) as [number, number, number, number], [basins]);

  const highlightFilter = React.useMemo(() => {
    const normalizedSites = contributingSites
      .map((site) => normalizeSiteName(site))
      .filter(Boolean);
    if (normalizedSites.length === 0) {
      return ['==', ['get', 'site_name_normalized'], '__none__'];
    }
    return ['in', ['get', 'site_name_normalized'], ['literal', normalizedSites]];
  }, [contributingSites]);

  const fillColor = React.useMemo(
    () => buildFillColorExpression(basinGroupNames),
    [basinGroupNames]
  );

  // Refit when the delineation data itself changes after the initial load.
  React.useEffect(() => {
    if (loaded) {
      mapRef.current?.fitBounds(bounds, {padding: 40});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds]);

  useResizeMapOnChange(mapRef, [expanded]);

  /*
  Throttled: each popup update re-renders the component, and mousemove fires at
  pointer-event rate the whole time a feature is hovered.
  */
  const handleMouseMove = React.useMemo(
    () =>
      throttle((e: InteractiveMouseEvent) => {
        const feature = e.features?.[0];
        if (!feature || !feature.properties) {
          setHoverPopup(null);
          return;
        }
        const {title, rows} = popupForFeature(
          feature.layer!.id,
          feature.properties as Record<string, unknown>
        );
        setHoverPopup({
          longitude: e.lngLat.lng,
          latitude: e.lngLat.lat,
          title,
          rows,
        });
      }, 50),
    []
  );

  return (
    <div className={`relative w-full ${expanded ? 'h-[700px]' : 'h-96'}`}>
      <div className="absolute left-2 top-2 z-10 flex gap-1 rounded bg-white/90 p-1 shadow-sm">
        <button
          type="button"
          className={`rounded px-2 py-1 text-xs ${basemapId === 'light' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          onClick={() => setBasemapId('light')}
        >
          Light
        </button>
        <button
          type="button"
          className={`rounded px-2 py-1 text-xs ${basemapId === 'streets' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          onClick={() => setBasemapId('streets')}
        >
          Streets
        </button>
        <button
          type="button"
          className={`rounded px-2 py-1 text-xs ${basemapId === 'satellite' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          onClick={() => setBasemapId('satellite')}
        >
          Satellite
        </button>
      </div>
      <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
        <button
          type="button"
          className="rounded bg-white/90 px-2 py-1 text-xs text-gray-700 shadow-sm hover:bg-gray-100"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Collapse map' : 'Expand map'}
        </button>
        <div className="rounded bg-white/90 p-2 shadow-sm text-xs">
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={layerVisibility.basins}
              onChange={(e) =>
                setLayerVisibility((prev) => ({
                  ...prev,
                  basins: e.target.checked,
                }))
              }
            />
            Basins
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={layerVisibility.rivers}
              onChange={(e) =>
                setLayerVisibility((prev) => ({
                  ...prev,
                  rivers: e.target.checked,
                }))
              }
            />
            Rivers
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={layerVisibility.forecastPoints}
              onChange={(e) =>
                setLayerVisibility((prev) => ({
                  ...prev,
                  forecastPoints: e.target.checked,
                }))
              }
            />
            <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0 border border-red-700 bg-red-500" />
            Forecast points
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={layerVisibility.privateSiteGauges}
              onChange={(e) =>
                setLayerVisibility((prev) => ({
                  ...prev,
                  privateSiteGauges: e.target.checked,
                }))
              }
            />
            <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0 border border-orange-700 bg-orange-500" />
            Private gauges
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={layerVisibility.ungaugedSites}
              onChange={(e) =>
                setLayerVisibility((prev) => ({
                  ...prev,
                  ungaugedSites: e.target.checked,
                }))
              }
            />
            <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0 border border-gray-500 bg-gray-400" />
            Ungauged points
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={layerVisibility.publicGauges}
              onChange={(e) =>
                setLayerVisibility((prev) => ({
                  ...prev,
                  publicGauges: e.target.checked,
                }))
              }
            />
            <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0 border border-violet-900 bg-violet-700" />
            Public gauges
          </label>
        </div>
        {basinGroupNames.length > 0 && (
          <div className="rounded bg-white/90 p-2 shadow-sm text-xs mt-1">
            <div className="font-medium mb-1">Basins</div>
            {basinGroupNames.map((name, i) => {
              const areaKm2 = drainageAreaByBasinGroup[name];
              return (
                <div key={name} className="flex items-center gap-1 mb-0.5">
                  <span
                    className="inline-block w-3 h-3 rounded-sm shrink-0"
                    style={{
                      backgroundColor: BASIN_PALETTE[i % BASIN_PALETTE.length],
                      opacity: 0.8,
                    }}
                  />
                  <span className="truncate max-w-[140px]">{name}</span>
                  {areaKm2 != null && (
                    <span className="text-gray-500 whitespace-nowrap ml-auto pl-1">
                      {areaKm2.toLocaleString()} km²
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="w-full h-full rounded border border-gray-200 overflow-hidden">
        <MapGL
          ref={mapRef}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          mapStyle={styleUrl}
          initialViewState={{bounds, fitBoundsOptions: {padding: 40}}}
          interactiveLayerIds={INTERACTIVE_LAYER_IDS}
          cursor={hoverPopup ? 'pointer' : 'auto'}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
            // cancel any pending trailing call so it can't reopen the popup
            handleMouseMove.cancel();
            setHoverPopup(null);
          }}
          onLoad={() => setLoaded(true)}
          style={{width: '100%', height: '100%'}}
        >
          <Source id="basins" type="geojson" data={basinsData}>
            <Layer
              id="basins-fill"
              type="fill"
              layout={visibility(layerVisibility.basins)}
              paint={{'fill-color': fillColor, 'fill-opacity': 0.3}}
            />
            <Layer
              id="basins-stroke"
              type="line"
              layout={visibility(layerVisibility.basins)}
              paint={{'line-color': fillColor, 'line-width': 1.5}}
            />
            <Layer
              id="basins-highlight"
              type="line"
              layout={visibility(layerVisibility.basins)}
              paint={BASINS_HIGHLIGHT_PAINT}
              filter={highlightFilter}
            />
          </Source>

          {rivers && (
            <Source id="rivers" type="geojson" data={rivers}>
              <Layer
                id="rivers-line"
                type="line"
                layout={visibility(layerVisibility.rivers)}
                paint={RIVERS_LINE_PAINT}
              />
            </Source>
          )}

          <Source id="sites" type="geojson" data={forecastPointsData}>
            <Layer
              id="sites-circle"
              type="circle"
              layout={visibility(layerVisibility.forecastPoints)}
              paint={SITES_CIRCLE_PAINT}
            />
            <Layer
              id="sites-label"
              type="symbol"
              layout={labelLayout(layerVisibility.forecastPoints)}
              paint={LABEL_PAINT}
            />
          </Source>

          <Source id="private-site-gauges" type="geojson" data={privateSiteGaugesData}>
            <Layer
              id="private-site-gauges-circle"
              type="circle"
              layout={visibility(layerVisibility.privateSiteGauges)}
              paint={PRIVATE_SITE_GAUGES_CIRCLE_PAINT}
            />
            <Layer
              id="private-site-gauges-label"
              type="symbol"
              layout={labelLayout(layerVisibility.privateSiteGauges)}
              paint={LABEL_PAINT}
            />
          </Source>

          <Source id="ungauged-sites" type="geojson" data={ungaugedSitesData}>
            <Layer
              id="ungauged-sites-circle"
              type="circle"
              layout={visibility(layerVisibility.ungaugedSites)}
              paint={UNGAUGED_SITES_CIRCLE_PAINT}
            />
            <Layer
              id="ungauged-sites-label"
              type="symbol"
              layout={labelLayout(layerVisibility.ungaugedSites)}
              paint={LABEL_PAINT}
            />
          </Source>

          <Source id="public-gauges" type="geojson" data={publicGaugesData}>
            <Layer
              id="public-gauges-circle"
              type="circle"
              layout={visibility(layerVisibility.publicGauges)}
              paint={PUBLIC_GAUGES_CIRCLE_PAINT}
            />
          </Source>

          {hoverPopup && (
            <Popup
              longitude={hoverPopup.longitude}
              latitude={hoverPopup.latitude}
              closeButton={false}
              closeOnClick={false}
            >
              <div className="text-xs">
                <div>
                  <strong>{hoverPopup.title}</strong>
                </div>
                {hoverPopup.rows
                  .filter(([, value]) => value != null && String(value).trim() !== '')
                  .map(([label, value]) => (
                    <div key={label}>
                      <strong>{label}:</strong> {String(value)}
                    </div>
                  ))}
              </div>
            </Popup>
          )}
        </MapGL>
      </div>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-500">Loading map…</span>
        </div>
      )}
    </div>
  );
}
